import { SearchOutlined } from '@ant-design/icons';
import { DatePicker, Form, Input, List, Select } from 'antd';
import CustomListItem from 'app/components/CustomListItem/CustomListItem';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import {
  selectAllCultures,
  selectCulturesLoading,
} from 'app/containers/Cultures/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { RouteConstants } from 'app/routes';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { useResourceSlice } from '../../slice';
import {
  selectResourcesByTaskCategoryId,
  selectSelectedTaskCategoryId,
  selectSelectedUsedResourceId,
} from '../../slice/selectors';
import { IWarehouseFilters, WarehouseFiltersEnum } from '../../slice/types';
import moment from 'moment';

import * as S from './TaskCategoryResourcesCard.styles';
import { toFixedIfFloat } from 'utils/helpers';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';

interface IProps {
  filters: IWarehouseFilters;
  setFilterValue: (
    name: WarehouseFiltersEnum,
    value: string | number | null,
  ) => void;
}

const TaskCategoryResourcesCard = ({ setFilterValue, filters }: IProps) => {
  const { t } = useTranslation();

  const history = useHistory();

  const { actions } = useResourceSlice();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const envId = useSelector(selectSelectedEnvId);
  const selectedCategoryId = useSelector(selectSelectedTaskCategoryId);
  const cultures = useSelector(selectAllCultures);
  const culturesLoading = useSelector(selectCulturesLoading);
  const { resourcesArr, loading } = useSelector(
    selectResourcesByTaskCategoryId,
  );
  const selectedResourceId = useSelector(selectSelectedUsedResourceId);
  const companyId = useSelector(selectSelectedCompanyId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const seasonId = useSelector(selectSelectedSeasonId);

  useEffect(() => {
    if (selectedCategoryId && envId && seasonId) {
      dispatch(
        actions.getResourcesByTaskCategoryId({
          envId,
          taskCategoryId: selectedCategoryId,
          companyId,
          companyGroupId,
          filters,
          seasonId,
        }),
      );
    }
  }, [
    dispatch,
    selectedCategoryId,
    envId,
    actions,
    companyId,
    companyGroupId,
    filters,
    seasonId,
  ]);

  const onListItemClick = (resourceId: number) => () => {
    if (envId && selectedCategoryId) {
      history.push(
        generatePath(
          RouteConstants.resource.resourceAllocationUsedResourceActionLog,
          {
            envId,
            taskCategoryId: selectedCategoryId,
            usedResourceId: resourceId,
          },
        ),
      );
    }
  };

  if (!selectedCategoryId) {
    return null;
  }

  return (
    <S.ListCard
      bordered={false}
      title={t('Resource.TaskCategoryResourcesCard.Title', 'Used resources')}
    >
      <Form
        layout={'horizontal'}
        form={form}
        initialValues={{ layout: 'horizontal' }}
        requiredMark="optional"
        id={'some id'}
        onFieldsChange={changedFields => {
          const name = changedFields[0]?.name.toString() as WarehouseFiltersEnum;
          const value = changedFields[0]?.value;

          if (name === WarehouseFiltersEnum.date) {
            if (value && Array.isArray(value)) {
              const startDate = value[0]
                ? moment(value[0]).format('YYYY-MM-DD')
                : null;

              const endDate = value[1]
                ? moment(value[1]).format('YYYY-MM-DD')
                : null;

              setFilterValue(WarehouseFiltersEnum.startDate, startDate);
              setFilterValue(WarehouseFiltersEnum.endDate, endDate);
            } else {
              setFilterValue(WarehouseFiltersEnum.startDate, null);
              setFilterValue(WarehouseFiltersEnum.endDate, null);
            }
          } else {
            setFilterValue(name, value);
          }
        }}
        style={{ margin: '0 24px 20px' }}
      >
        <Form.Item name={WarehouseFiltersEnum.search}>
          <Input
            prefix={<SearchOutlined />}
            placeholder={t(
              'Resource.TaskCategoryResourcesCard.SearchPlaceholder',
              'Search',
            )}
          />
        </Form.Item>
        <Form.Item name={WarehouseFiltersEnum.date}>
          <DatePicker.RangePicker allowClear allowEmpty={[true, true]} />
        </Form.Item>
        <Form.Item
          name={WarehouseFiltersEnum.cultureId}
          style={{ marginBottom: 0 }}
        >
          <Select
            placeholder={t(
              'Resource.TaskCategoryResourcesCard.Select culture',
              'Select culture',
            )}
            allowClear
            loading={culturesLoading}
            getPopupContainer={trigger => trigger.parentNode}
          >
            {cultures.map(culture => (
              <Select.Option key={culture.id} value={culture.id}>
                {culture.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>

      <List
        loading={loading}
        itemLayout="horizontal"
        dataSource={resourcesArr}
        renderItem={resource => (
          <CustomListItem
            key={resource.resource.id}
            isActive={selectedResourceId === resource.resource.id}
            onClick={onListItemClick(resource.resource.id)}
          >
            <List.Item.Meta
              title={resource.resource.name}
              description={`${toFixedIfFloat(resource.amount)} ${
                resource.resource.type.unit
              }`}
            />
          </CustomListItem>
        )}
      />
    </S.ListCard>
  );
};

export default TaskCategoryResourcesCard;
